import { isWithinInterval, startOfDay } from 'date-fns';
import {
  PurchaseOrderItem,
  PurchaseOrderItemStatus,
} from '@domain/purchasing';
import { STATUS_RANK } from './status';
import { MilestoneKind, StatusMilestone } from './types';

/** Minimum status rank at which this milestone is considered completed. */
const MILESTONE_REACHED_RANK: Record<MilestoneKind, number> = {
  production_start: 1,
  production_ready: 2,
  departure: 3,
  arrival: 4,
  processed: 6,
  eta: 6,
};

/** All scheduled status-change dates for an item, regardless of current status. */
export const getStatusMilestones = (
  item: PurchaseOrderItem
): StatusMilestone[] => {
  const milestones: StatusMilestone[] = [];

  if (item.productionStartDate) {
    milestones.push({
      date: startOfDay(item.productionStartDate),
      kind: 'production_start',
    });
  }
  if (item.productionReadyDate) {
    milestones.push({
      date: startOfDay(item.productionReadyDate),
      kind: 'production_ready',
    });
  }
  if (item.shipment?.departureDate) {
    milestones.push({
      date: startOfDay(item.shipment.departureDate),
      kind: 'departure',
    });
  }
  if (item.shipment?.arrivalDate) {
    milestones.push({
      date: startOfDay(item.shipment.arrivalDate),
      kind: 'arrival',
    });
  }
  if (item.shipment?.processedDate) {
    milestones.push({
      date: startOfDay(item.shipment.processedDate),
      kind: 'processed',
    });
  } else if (item.eta) {
    milestones.push({
      date: startOfDay(item.eta),
      kind: 'eta',
    });
  }

  return milestones;
};

/** Date when the item's current status phase ends. */
export const getStatusEndDate = (
  item: PurchaseOrderItem
): Date | undefined => {
  switch (item.status) {
    case PurchaseOrderItemStatus.PLANNED:
      return item.productionStartDate ?? item.productionReadyDate ?? item.eta;
    case PurchaseOrderItemStatus.IN_PRODUCTION:
      return item.productionReadyDate ?? item.eta;
    case PurchaseOrderItemStatus.PRODUCTION_READY:
      return item.shipment?.departureDate ?? item.eta;
    case PurchaseOrderItemStatus.IN_TRANSIT:
      return item.shipment?.arrivalDate ?? item.eta;
    case PurchaseOrderItemStatus.ARRIVED:
    case PurchaseOrderItemStatus.IN_DELIVERY:
    case PurchaseOrderItemStatus.AVAILABLE:
      return item.shipment?.processedDate ?? item.eta;
    default:
      return item.eta;
  }
};

/** Final warehouse-available date for overall ETA label. */
export const getFinalEta = (item: PurchaseOrderItem): Date | undefined => {
  if (item.shipment?.processedDate) return item.shipment.processedDate;
  if (item.eta) return item.eta;
  return item.productionReadyDate;
};

export const milestoneInWeek = (
  milestone: StatusMilestone,
  weekStart: Date,
  weekEnd: Date
): boolean =>
  isWithinInterval(milestone.date, { start: weekStart, end: weekEnd });

export const isMilestoneReached = (
  item: PurchaseOrderItem,
  kind: MilestoneKind
): boolean => STATUS_RANK[item.status] >= MILESTONE_REACHED_RANK[kind];

export const isMilestoneOverdue = (
  item: PurchaseOrderItem,
  milestone: StatusMilestone,
  today: Date
): boolean =>
  milestone.date < today && !isMilestoneReached(item, milestone.kind);

/** Status reached when confirming a milestone for its week. */
export const statusForMilestone = (
  kind: MilestoneKind
): PurchaseOrderItemStatus => {
  switch (kind) {
    case 'production_start':
      return PurchaseOrderItemStatus.IN_PRODUCTION;
    case 'production_ready':
      return PurchaseOrderItemStatus.PRODUCTION_READY;
    case 'departure':
      return PurchaseOrderItemStatus.IN_TRANSIT;
    case 'arrival':
      return PurchaseOrderItemStatus.ARRIVED;
    case 'processed':
    case 'eta':
      return PurchaseOrderItemStatus.AVAILABLE;
  }
};

/** Latest overdue milestone (furthest along) that is past due and not yet reached. */
export const getLatestOverdueMilestone = (
  item: PurchaseOrderItem,
  today: Date
): StatusMilestone | undefined => {
  const overdue = getStatusMilestones(item)
    .filter(
      (milestone) =>
        milestone.date < today && !isMilestoneReached(item, milestone.kind)
    )
    .sort((a, b) => {
      const byRank =
        MILESTONE_REACHED_RANK[a.kind] - MILESTONE_REACHED_RANK[b.kind];
      if (byRank !== 0) return byRank;
      return a.date.getTime() - b.date.getTime();
    });

  return overdue[overdue.length - 1];
};

/** True when any scheduled milestone is past due and not yet reached. */
export const isItemOverdue = (
  item: PurchaseOrderItem,
  today: Date
): boolean => {
  if (item.status === PurchaseOrderItemStatus.AVAILABLE) return false;
  return getLatestOverdueMilestone(item, today) != null;
};

/** Prefer the latest milestone in the selected week; otherwise the current-status end date. */
export const getDisplayMilestone = (
  item: PurchaseOrderItem,
  weekStart: Date,
  weekEnd: Date
): StatusMilestone | undefined => {
  const inWeek = getStatusMilestones(item)
    .filter((m) => milestoneInWeek(m, weekStart, weekEnd))
    .sort((a, b) => {
      const byDate = a.date.getTime() - b.date.getTime();
      if (byDate !== 0) return byDate;
      return MILESTONE_REACHED_RANK[a.kind] - MILESTONE_REACHED_RANK[b.kind];
    });
  if (inWeek.length > 0) return inWeek[inWeek.length - 1];

  const statusEnd = getStatusEndDate(item) ?? getFinalEta(item);
  if (!statusEnd) return undefined;
  return { date: startOfDay(statusEnd), kind: 'eta' };
};
